/**
 * Client-side image resizer.
 *
 * Phone photos are typically 4032×3024 (~10 MB). Kinsy displays them at
 * ≤1200px and prints at up to 12×16″. Resizing to 2048px max edge:
 *   - cuts upload payload by ~95%
 *   - still gives 170 DPI on a 12″ wide print (well above readable)
 *   - drops a 30 sec upload to ~1 sec on LTE
 *
 * Non-image files, HEIC files (handled server-side), and already-small
 * files pass through untouched.
 */

const MAX_EDGE = 2048;
const QUALITY = 0.88;
const RESIZE_THRESHOLD_BYTES = 800 * 1024;  // Don't bother resizing files <800 KB
// Browsers don't expose a HEIC decoder — let the server handle conversion.
// (Otherwise we'd corrupt the file by trying to drawImage a HEIC blob.)
const SKIP_TYPES = new Set(['image/heic', 'image/heif']);

function loadImage(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

/**
 * Resize an image File/Blob to MAX_EDGE on the longest side, JPEG-encoded.
 * Returns the original File untouched if:
 *   - it's not an image
 *   - it's HEIC/HEIF (let the server convert)
 *   - it's already smaller than MAX_EDGE in both dimensions
 *   - the resized output ends up larger than the original (rare but possible)
 *   - any error occurs (graceful fallback — never block the upload)
 */
export async function resizeImage(file) {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (SKIP_TYPES.has(file.type)) return file;
  if (file.size < RESIZE_THRESHOLD_BYTES) return file;

  try {
    const img = await loadImage(file);
    const { width, height } = img;
    if (width <= MAX_EDGE && height <= MAX_EDGE) return file;

    const scale = MAX_EDGE / Math.max(width, height);
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
    if (!blob || blob.size >= file.size) return file;

    const newName = file.name?.replace(/\.(png|webp|jpeg|jpg|gif|bmp|tiff?)$/i, '.jpg') || 'photo.jpg';
    return new File([blob], newName, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;  // Never break uploads because of resize failure
  }
}
